import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getStatusColor } from '../../utils';
import { formatFieldName, formatFieldValue, generateEditMessage } from '../../utils/formatters';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import RelativeTime from '../RelativeTime';
import EditableComment from './EditableComment';
import CommentForm from '../../components/CommentForm';

// Utility to combine class names
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Using shared statusOptions from utils/helpers.js

// Activity Feed component to display comments and edit history
function ActivityFeed({ scope, onAddComment }) {
  const { user } = useAuth();
  
  // Early return if scope is not loaded
  if (!scope) return null;
  
  // Combine edit history and comments into a single activity array
  const createActivityItems = () => {
    const items = [];
    
    // Add edit history entries
    if (Array.isArray(scope.edit_history)) {
      scope.edit_history.forEach((edit, index) => {
        items.push({
          id: `edit-${index}`,
          type: 'edit',
          timestamp: edit.timestamp,
          user: edit.user,
          field: edit.field,
          oldValue: edit.old_value,
          newValue: edit.new_value
        });
      });
    }
    
    // Add comment entries
    if (Array.isArray(scope.comments)) {
      scope.comments.forEach(comment => {
        items.push({
          id: comment.id || `comment-${items.length}`,
          type: 'comment',
          timestamp: comment.timestamp,
          user: comment.user,
          content: comment.content,
          edited: comment.edited,
          files: comment.files || []
        });
      });
    }
    
    // Sort by timestamp, newest first
    // Handle both timestamp formats (ISO string and milliseconds since epoch)
    return items.sort((a, b) => {
      // Convert timestamps to numbers for comparison
      const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
      const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
      return timeB - timeA;
    });
  };
  
  const activityItems = createActivityItems();
  
  // Return empty state if no activity and no comment form
  if (activityItems.length === 0 && !onAddComment) {
    return (
      <div className="mt-4 text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No activity recorded for this budget item.</p>
      </div>
    );
  }
  
  // Using shared functions from utils/formatters.js
  // Using shared getStatusColor from utils/helpers.js
  
  // Handler for successful comment edit or delete
  const handleCommentUpdateSuccess = () => {
    // Call onAddComment with null to trigger a refresh without adding a comment
    if (onAddComment) {
      onAddComment(null, null, true);
    }
  };
  
  return (
    <div className="flow-root mt-4">
      {/* Comment form */}
      {onAddComment && (
        <div className="mb-6">
          <CommentForm
            onSubmit={onAddComment}
            placeholder="Add your comment..."
            submitLabel="Comment"
          />
        </div>
      )}

      <ul className="-mb-8">
        {activityItems.map((item, index) => (
          <li key={item.id}>
            <div className="relative pb-4">
              {index !== activityItems.length - 1 ? (
                <span aria-hidden="true" className="absolute top-5 left-5 -ml-px h-[calc(100%-5px)] w-0.5 bg-gray-200 dark:bg-gray-700" />
              ) : null}
              
              <div className="relative flex items-start space-x-3">
                {item.type === 'comment' ? (
                  <>
                    <div className="relative">
                      <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/60 ring-8 ring-white dark:ring-gray-800">
                        <span className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">
                          {item.user.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <span className="absolute -right-1 -bottom-0.5 rounded-tl bg-white dark:bg-gray-800 px-0.5 py-px">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5 text-gray-400">
                          <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 0 0 1.33 0l1.713-3.293a.783.783 0 0 1 .642-.413 41.102 41.102 0 0 0 3.55-.414c1.437-.231 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div>
                        <div className="text-sm">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {item.user}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                          Commented <RelativeTime timestamp={item.timestamp} />
                          {item.edited && (
                            <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
                              <RelativeTime timestamp={item.edited.timestamp} showEditedPrefix={true} />
                            </span>
                          )}
                          {/* Show file attachment indicator if comment has files */}
                          {item.files && item.files.length > 0 && (
                            <span className="ml-2 inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <PaperClipIcon className="h-3.5 w-3.5 mr-0.5" />
                              {item.files.length}
                            </span>
                          )}
                        </p>
                      </div>
                      <EditableComment
                        comment={item}
                        shortName={scope.short_name}
                        onEditSuccess={handleCommentUpdateSuccess}
                        onDeleteSuccess={handleCommentUpdateSuccess}
                      />
                    </div>
                  </>
                ) : item.type === 'edit' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 ring-8 ring-white dark:ring-gray-800">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5 text-gray-500 dark:text-gray-300">
                            <path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
                            <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {item.user}
                        </span>{' '}
                        
                        {/* Render edit message based on field type */}
                        {item.field === 'status' ? (
                          <>
                            changed status from{' '}
                            <span className={classNames("inline-block px-2 py-0.5 text-xs font-semibold rounded-full", getStatusColor(item.oldValue))}>
                              {item.oldValue}
                            </span>{' '}
                            to{' '}
                            <span className={classNames("inline-block px-2 py-0.5 text-xs font-semibold rounded-full", getStatusColor(item.newValue))}>
                              {item.newValue}
                            </span>
                          </>
                        ) : item.field === 'percentage_complete' ? (
                          <>
                            updated {formatFieldName(item.field)} from{' '}
                            <span className="font-medium text-blue-600 dark:text-blue-400">
                              {formatFieldValue(item.field, item.oldValue)}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium text-blue-600 dark:text-blue-400">
                              {formatFieldValue(item.field, item.newValue)}
                            </span>
                          </>
                        ) : item.field === 'notes' ? (
                          <>
                            updated notes
                            <div className="mt-2 mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md text-gray-600 dark:text-gray-300 text-xs">
                              <div className="mb-2">
                                <span className="font-semibold">Previous:</span>
                                <div className="pl-2 border-l-2 border-gray-300 dark:border-gray-600 mt-1 whitespace-pre-wrap">
                                  {item.oldValue || <span className="italic text-gray-400 dark:text-gray-500">Empty</span>}
                                </div>
                              </div>
                              <div>
                                <span className="font-semibold">Updated:</span>
                                <div className="pl-2 border-l-2 border-indigo-300 dark:border-indigo-600 mt-1 whitespace-pre-wrap">
                                  {item.newValue || <span className="italic text-gray-400 dark:text-gray-500">Empty</span>}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (['contract_added', 'contract_deleted', 'contract_updated', 
                            'payment_added', 'payment_deleted', 'payment_updated'].includes(item.field)) ? (
                          <>
                            {generateEditMessage(item.field, item.oldValue, item.newValue)}
                          </>
                        ) : (item.oldValue === null || item.oldValue === undefined || item.oldValue === '') && 
                           (item.newValue !== null && item.newValue !== undefined && item.newValue !== '') ? (
                          <>
                            added {formatFieldName(item.field)}{' '}
                            <span className="font-medium dark:text-white">
                              {formatFieldValue(item.field, item.newValue)}
                            </span>
                          </>
                        ) : (item.newValue === null || item.newValue === undefined || item.newValue === '') && 
                          (item.oldValue !== null && item.oldValue !== undefined && item.oldValue !== '') ? (
                          <>
                            removed {formatFieldName(item.field)}{' '}
                            <span className="font-medium dark:text-white">
                              {formatFieldValue(item.field, item.oldValue)}
                            </span>
                          </>
                        ) : (
                          <>
                            updated {formatFieldName(item.field)} from{' '}
                            <span className="font-medium dark:text-white">
                              {formatFieldValue(item.field, item.oldValue)}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium dark:text-white">
                              {formatFieldValue(item.field, item.newValue)}
                            </span>
                          </>
                        )}
                        
                        {' '}
                        <RelativeTime timestamp={item.timestamp} />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ActivityFeed;